/**
 * Tiny deterministic renderer for the markdown subset our own composers emit (annexure content_md,
 * pass_rule_md): #/##/### headings, **bold**, | | tables, ordered/unordered lists, paragraphs.
 * Escape-first — content is self-generated but may embed user text. A full markdown lib is overkill
 * for this one surface; tables are the piece raw text can't show.
 */
import { Fragment } from 'react';

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
/** escape, then re-introduce <strong> for **bold** */
function inline(s: string): string {
  return esc(s).replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
}

type Block =
  | { kind: 'h'; level: number; text: string }
  | { kind: 'table'; rows: string[][] }
  | { kind: 'list'; ordered: boolean; items: string[] }
  | { kind: 'p'; text: string };

function parse(md: string): Block[] {
  const lines = md.split('\n');
  const blocks: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const t = line.trim();
    if (!t) { i++; continue; }
    const h = t.match(/^(#{1,3})\s+(.*)$/);
    if (h) { blocks.push({ kind: 'h', level: h[1].length, text: h[2] }); i++; continue; }
    if (/^\|.*\|$/.test(t)) {
      const rows: string[][] = [];
      while (i < lines.length && /^\|.*\|$/.test(lines[i].trim())) {
        const cells = lines[i].trim().slice(1, -1).split('|').map((c) => c.trim());
        if (!cells.every((c) => /^[\s:-]*$/.test(c))) rows.push(cells); // skip the --- separator
        i++;
      }
      blocks.push({ kind: 'table', rows });
      continue;
    }
    const li = t.match(/^(\d+\.|[-*])\s+(.*)$/);
    if (li) {
      const ordered = /^\d+\./.test(t);
      const items: string[] = [];
      while (i < lines.length) {
        const m = lines[i].trim().match(/^(\d+\.|[-*])\s+(.*)$/);
        if (!m) break;
        items.push(m[2]); i++;
      }
      blocks.push({ kind: 'list', ordered, items });
      continue;
    }
    // paragraph: gather until a blank/structural line
    const para: string[] = [];
    while (i < lines.length && lines[i].trim() && !/^(#{1,3})\s|^\|.*\|$|^(\d+\.|[-*])\s/.test(lines[i].trim())) {
      para.push(lines[i].trim()); i++;
    }
    blocks.push({ kind: 'p', text: para.join('\n') });
  }
  return blocks;
}

export default function MdView({ md, className }: { md: string | null | undefined; className?: string }) {
  if (!md?.trim()) return null;
  const blocks = parse(md);
  return (
    <div className={className}>
      {blocks.map((b, i) => {
        if (b.kind === 'h') {
          const Tag = (['h3', 'h4', 'h5'][b.level - 1] ?? 'h5') as 'h3' | 'h4' | 'h5';
          return <Tag key={i} dangerouslySetInnerHTML={{ __html: inline(b.text) }} />;
        }
        if (b.kind === 'table') {
          const [head, ...body] = b.rows;
          return (
            <div key={i} className="cmp-wrap" style={{ margin: '6px 0' }}>
              <table className="cmp">
                {head && <thead><tr>{head.map((c, j) => <th key={j} dangerouslySetInnerHTML={{ __html: inline(c) }} />)}</tr></thead>}
                <tbody>{body.map((r, ri) => <tr key={ri}>{r.map((c, j) => <td key={j} dangerouslySetInnerHTML={{ __html: inline(c) }} />)}</tr>)}</tbody>
              </table>
            </div>
          );
        }
        if (b.kind === 'list') {
          const items = b.items.map((it, j) => <li key={j} dangerouslySetInnerHTML={{ __html: inline(it) }} />);
          return b.ordered ? <ol key={i}>{items}</ol> : <ul key={i}>{items}</ul>;
        }
        return (
          <p key={i} style={{ whiteSpace: 'pre-wrap', margin: '6px 0' }}>
            {b.text.split('\n').map((ln, j) => (
              <Fragment key={j}>{j > 0 && <br />}<span dangerouslySetInnerHTML={{ __html: inline(ln) }} /></Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
}
